import { Drawer, Typography, Space, Tag, Image, Tooltip } from 'antd'
import styled from 'styled-components'
import { IconFont, TagDetail, BaseDrawer } from '@/components'
import { useGetCaseDetail, useAttackTypeList } from './utils'
import { useEffect, useMemo } from 'react'
import moment from 'moment'
import leakSeverity from '@/assets/imges/leak-severity.png'
import leakCenter from '@/assets/imges/leak-center.png'
import leakLow from '@/assets/imges/leak-low.png'
import leakHigh from '@/assets/imges/leak-high.png'
import { t } from '@/i18n'
interface CaseDetailProps {
  visible: boolean
  onClose: () => void
  resourceId: string
  type: 'url' | 'attachment'
  attackTypeList: any[]
}

const attachListMap = {
  OfficeDocument: t('office文档'),
  ScriptFile: t('脚本文件'),
  PeExecutable: t('PE可执行文件'),
  ElfFormat: t('可执行与可链接格式'),
  CalendarFile: t('日历文件'),
  WebPageFile: t('网页文件'),
  CompressedFile: t('压缩文件'),
}

export const CaseDetail = ({
  visible,
  onClose,
  resourceId,
  type,
  attackTypeList,
}: CaseDetailProps) => {
  const { Text } = Typography
  const [detail, getCaseDetail, loading] = useGetCaseDetail()
  useEffect(() => {
    if (visible) {
      getCaseDetail({ resourceId, type }).catch(_ => {})
    }
  }, [visible])

  const infoKey = useMemo(() => {
    if (type === 'url') {
      return 'urlInfo'
    }
    return 'attachmentInfo'
  }, [type])

  const renderLabel = (label: string) => (
    <Text className="label" type="secondary">
      {label}：
    </Text>
  )

  const riskLevelMap = {
    RiskLevelLow: { label: t('低危'), class: 'leak-low', icon: <Image src={leakLow} width={19} /> },
    RiskLevelMiddle: {
      label: t('中危'),
      class: 'leak-center',
      icon: <Image src={leakCenter} width={19} />,
    },
    RiskLevelHigh: {
      label: t('高危'),
      class: 'leak-high',
      icon: <Image src={leakHigh} width={19} />,
    },
    RiskLevelGross: {
      label: t('严重'),
      class: 'leak-severity',
      icon: <Image src={leakSeverity} width={19} />,
    },
  }

  const resourceLevelMap = {
    ResourceLevelJunior: { label: t('初级'), class: 'resource-level-junior' },
    ResourceLevelMiddle: { label: t('中级'), class: 'resource-level-middle' },
    ResourceLevelSenior: { label: t('高级'), class: 'resource-level-senior' },
  }

  return (
    <BaseDrawer title={t('详情')} onClose={onClose} open={visible} props={{ width: 560 }}>
      <Container>
        <div className="content">
          <div className="item" style={{ display: 'flex' }}>
            <span style={{ width: '100px', display: 'inline-block' }}>
              {renderLabel(t('用例名称'))}
            </span>
            <Typography.Text ellipsis={{ tooltip: true }} style={{ width: '100%' }}>
              {detail?.baseInfo?.name}
            </Typography.Text>
          </div>
          <div className="item">
            {renderLabel(t('验证模式'))}
            <Text className="content">
              {detail?.baseInfo?.verificationModeId === 8 ? t('平台 → 邮箱') : t('平台 → 邮箱')}
            </Text>
          </div>
          <div className="item template">
            {renderLabel(t('模板名称'))}
            {detail?.[infoKey]?.templateNames?.length > 0 ? (
              <Text className="content ">
                {detail?.[infoKey]?.templateNames?.join(', ') || '-'}
              </Text>
            ) : (
              <Text className="content">-</Text>
            )}
          </div>
          {type === 'attachment' && (
            <>
              <div className="item">
                {renderLabel(t('附件类型'))}
                <Text className="content">
                  {attachListMap[
                    detail?.attachmentInfo?.attachmentType as keyof typeof attachListMap
                  ] || '-'}
                </Text>
              </div>
              <div className="item">
                {renderLabel(t('附件'))}
                <Text className="content">{detail?.attachmentInfo?.attachmentName}</Text>
              </div>
              <div className="item">
                {renderLabel(t('操作系统'))}
                <Text className="content">
                  {detail?.[infoKey]?.os === 'OsWindows'
                    ? 'Windows'
                    : detail?.[infoKey]?.os === 'OsLinux'
                    ? 'Linux'
                    : detail?.[infoKey]?.os === 'OsMac'
                    ? 'Mac OS'
                    : detail?.[infoKey]?.os === 'OsCommon'
                    ? t('通用')
                    : '-'}
                </Text>
              </div>
            </>
          )}
          {type === 'url' && (
            <>
              <div className="item" style={{ display: 'flex' }}>
                <span style={{ width: '100px', display: 'inline-block' }}>
                  {renderLabel('URL')}
                </span>
                <Typography.Text
                  ellipsis={{
                    tooltip: {
                      getPopupContainer: () => document.body,
                    },
                  }}
                  className="content"
                >
                  {detail?.urlInfo?.url}
                </Typography.Text>
              </div>
              <div className="item">
                {renderLabel(t('攻击组织'))}
                <Text className="content">{detail?.[infoKey]?.aptName || '-'}</Text>
              </div>
            </>
          )}
          <div className="item">
            {renderLabel(t('危险等级'))}
            <span>
              {riskLevelMap[detail?.baseInfo?.riskLevel]?.icon}
              <span className={riskLevelMap[detail?.baseInfo?.riskLevel]?.class}>
                {riskLevelMap[detail?.baseInfo?.riskLevel]?.label || '-'}
              </span>
            </span>
          </div>
          <div className="item">
            {renderLabel(t('用例分级'))}
            <span className={resourceLevelMap[detail?.baseInfo?.resourceLevel]?.class}>
              {resourceLevelMap[detail?.baseInfo?.resourceLevel]?.label}
            </span>
          </div>
          <div className="item">
            {renderLabel(t('用例类型'))}
            <Text className="content">
              {detail?.baseInfo?.resourceType === 'ResourceTypeMailSecurityAttachment'
                ? t('附件')
                : 'URL'}
            </Text>
          </div>
          <div className="item">
            {renderLabel(t('攻击类型'))}
            <Text className="content">{detail?.[infoKey]?.attackTypeName || '-'}</Text>
          </div>
          {detail?.[infoKey]?.attckNames?.length > 0 ? (
            <div className="item" style={{ display: 'flex', flexDirection: 'column' }}>
              {renderLabel('ATT&CK')}

              <div style={{ background: '#F8F9FB', padding: 10, borderRadius: 8, marginTop: 14 }}>
                {detail?.[infoKey]?.attckNames?.map((info, index) => (
                  <Text
                    key={index}
                    className="content"
                    style={{ display: 'block', marginBottom: 5 }}
                  >
                    {info}
                  </Text>
                ))}
              </div>
            </div>
          ) : (
            <div className="item">
              {renderLabel('ATT&CK')}
              <Text className="content">-</Text>
            </div>
          )}
          <div className="item">
            {renderLabel(t('更新时间'))}
            <Text className="content">
              {detail?.[infoKey]?.updateTime
                ? moment(detail?.[infoKey]?.updateTime).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Text>
          </div>
          <div className="item">
            {detail?.[infoKey]?.tagCategoryList &&
              detail?.[infoKey]?.tagCategoryList?.length > 0 && (
                <TagDetail resourceTagList={detail?.[infoKey]?.tagCategoryList} />
              )}
          </div>
          <div className="item">
            {renderLabel(t('描述'))}
            <div className="description">{detail?.[infoKey]?.describe}</div>
          </div>
          <div className="item">
            {renderLabel(t('解决方案'))}
            <div className="description">{detail?.[infoKey]?.solution}</div>
          </div>
        </div>
      </Container>
    </BaseDrawer>
  )
}

const Container = styled.div`
  .content {
    .item {
      margin-bottom: 24px;

      .label {
        display: inline-block;
        width: 100px;
        color: #86909c;
        font-size: 14px;
      }

      .content {
        color: #333;
        font-size: 14px;
      }
    }

    .description {
      margin-top: 8px;
      padding: 16px 12px;
      background: #f8f9fb;
      border-radius: 8px;
      min-height: 80px;
      white-space: pre-wrap;
      word-break: break-all;
    }
  }
  .template {
    display: flex;
    .content {
      width: calc(100% - 100px);
      display: inline-block;
      line-height: 1.6em;
    }
  }
  .leak-low {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #e1ebfe;
    color: #6f9fff;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .leak-center {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #ffedce;
    color: #ffa709;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .leak-high {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #ffe1e1;
    color: #ff4141;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .leak-severity {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #f5d9d9;
    color: #b70000;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }
  .resource-level-junior {
    display: inline-block;
    background: #6f9fff;
    color: #fff;
    border-radius: 4px;
    font-size: 12px;
    width: 36px;
    height: 16px;
    line-height: 16px;
    text-align: center;
  }
  .resource-level-middle {
    display: inline-block;
    background: #ffa709;
    color: #fff;
    border-radius: 4px;
    font-size: 12px;
    width: 36px;
    height: 16px;
    line-height: 16px;
    text-align: center;
  }
  .resource-level-senior {
    display: inline-block;
    background: #f44f4f;
    color: #fff;
    border-radius: 4px;
    font-size: 12px;
    width: 36px;
    height: 16px;
    line-height: 16px;
    text-align: center;
  }
`

export default CaseDetail
